import type { CycleEntry, ProfilingState } from "./types";
import type { Trace } from "../tracing/types";
import { tracesInWindow } from "../tracing/selectors";
import { groupSummaries, type AggregateRow, type GroupSummary } from "./selectors";
import { filterGroups, filteredAggregateRows, groupEventWindows, type ProfilingFilters } from "./filtering";

/**
 * One render pass in an export: the pass's summary metrics plus its raw cycles (every batch with
 * node-level timings) and the trace events in its causal window.  `rootCycle`/`cycleIds` are
 * dropped — both are redundant with `cycles`.
 */
export type ExportedPass = Omit<GroupSummary, "rootCycle" | "cycleIds"> & {
    events: Trace[];
    cycles: CycleEntry[];
};

export type ProfilingExport = {
    kind: "qerent-profiling-export";
    version: 1;
    exportedAt: string;
    /** The filters that were active — the passes/aggregate below are what survived them. */
    filters: ProfilingFilters;
    /** Filtered passes, chronological (startTime asc). */
    passes: ExportedPass[];
    /** The Aggregate view's rows under the same filters (files/sources act row-level here). */
    aggregate: AggregateRow[];
};

/**
 * The Profiling tab's export document: everything the tab currently shows under the active filters,
 * with the raw node-level data so it can be re-analyzed offline.  `meta` is injected (rather than
 * read from the clock/build here) to keep the builder pure.
 */
export function buildProfilingExport(
    profiling: ProfilingState,
    traces: Trace[],
    filters: ProfilingFilters,
    meta: { exportedAt: string; },
): ProfilingExport {
    const allGroups = groupSummaries(profiling);
    const visible = filterGroups(allGroups, profiling, traces, filters)
        .toSorted((a, b) => a.startTime - b.startTime);
    // Windows are relative to ALL retained passes — eviction/filtering must not widen a window.
    const windows = groupEventWindows(allGroups, profiling.startedAtPerf ?? -Infinity);

    const passes = visible.map(({ rootCycle: _root, cycleIds, ...metrics }): ExportedPass => {
        const win = windows.get(metrics.rootCycleId);
        return {
            ...metrics,
            events: win ? tracesInWindow(traces, win.fromPerf, win.toPerf) : [],
            cycles: cycleIds.map(id => profiling.cyclesById[id]).filter(Boolean),
        };
    });

    return {
        kind: "qerent-profiling-export",
        version: 1,
        exportedAt: meta.exportedAt,
        filters,
        passes,
        aggregate: filteredAggregateRows(profiling, traces, filters),
    };
}
